import * as anchor from "@project-serum/anchor";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Container, Slide, Snackbar, ThemeProvider } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { AlertTitle, ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import Alert from "@material-ui/lab/Alert";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import { WalletMultiButton } from "@solana/wallet-adapter-material-ui";
import { useWallet } from "@solana/wallet-adapter-react";
import {
  Commitment,
  Connection,
  LAMPORTS_PER_SOL,
  PublicKey,
  Transaction,
} from "@solana/web3.js";
import { alternativeTheme } from "./app.theme";
import {
  awaitTransactionSignatureConfirmation,
  CandyMachineAccount,
  ClaimTxnState,
  claimWLTokens,
  createAccountsForMint,
  exchangeNFTforWLToken,
  ExchangeTxnState,
  getCandyMachineState,
  getCollectionPDA,
  mintOneToken,
  SetupState,
} from "./candy-machine";
import { MintButton } from "./MintButton";
import { MintCountdown } from "./MintCountdown";
import { AlertState, formatNumber, getAtaForMint, toDate } from "./utils";
// import {
//   fetchDistributor,
//   getClaimStatus,
//   getGumdropInfo,
//   getProgram,
//   hasBeenClaimed,
// } from "./gumdrop";

const WL_TOKEN = process.env.REACT_APP_WL_TOKEN!;
const WL_NAME = "Seers WL";
const WL_IMAGE =
  "https://www.arweave.net/VCJV8AvrrVzJGu4dDdG3wk2UsrvYtHM057fOV_xA7mU?ext=jpg";

function TransitionUp(props: any) {
  return <Slide {...props} direction="up" />;
}

const FEE_PRICE = 0.013;
export interface HomeProps {
  candyMachineId?: anchor.web3.PublicKey;
  connection: anchor.web3.Connection;
  txTimeout: number;
  rpcHost: string;
  network: WalletAdapterNetwork;
  error?: string;
}

interface TokenAccountInfo {
  pubkey: PublicKey;
  info: any;
}

export interface MintOption {
  type: "wl" | "nft";
  mint: string;
  account: string;
  amount: number;
  name: string;
  image: string;
}

const Home = (props: HomeProps) => {
  const [isUserMinting, setIsUserMinting] = useState(false);
  const [candyMachine, setCandyMachine] = useState<CandyMachineAccount>();
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    message: "",
    severity: undefined,
  });
  const [isActive, setIsActive] = useState(false);
  const [endDate, setEndDate] = useState<Date>();
  const [itemsRemaining, setItemsRemaining] = useState<number>();
  const [isWhitelistMint, setIsWhitelistMint] = useState(false);
  const [isWhitelistUser, setIsWhitelistUser] = useState(false);
  const [isPresale, setIsPresale] = useState(false);
  // eslint-disable-next-line
  const [isValidBalance, setIsValidBalance] = useState(false);
  // eslint-disable-next-line
  const [discountPrice, setDiscountPrice] = useState<anchor.BN>();
  // eslint-disable-next-line
  const [isSOLMint, setIsSOLMint] = useState(true);
  // eslint-disable-next-line
  const [needTxnClaim, setNeedTxnClaim] = useState(false);
  const [claimTxn, setClaimTxn] = useState<ClaimTxnState>();
  const [needTxnSplit, setNeedTxnSplit] = useState(true);
  const [setupTxn, setSetupTxn] = useState<SetupState>();

  const [allowList] = useState<any>({});
  const [whitelistTokenAccount, setWhitelistTokenAccount] = useState<
    PublicKey | undefined
  >();
  const [tokenAccounts, setTokenAccounts] = useState<TokenAccountInfo[]>([]);
  const [selectedOption, setSelectedOption] = useState<
    MintOption | undefined
  >();

  const mintOptions = useMemo(() => {
    const validMints = Object.keys(allowList);

    let result: MintOption[] = [];

    const wlTokenAccount = tokenAccounts.find(
      (t) => t.info.mint === WL_TOKEN && t.info.tokenAmount.uiAmount > 0
    );
    if (wlTokenAccount) {
      result = [
        ...result,
        {
          type: "wl",
          account: wlTokenAccount.pubkey.toBase58(),
          mint: WL_TOKEN,
          amount: wlTokenAccount.info.tokenAmount.uiAmount,
          name: WL_NAME,
          image: WL_IMAGE,
        },
      ];
    }

    result = [
      ...result,
      ...tokenAccounts
        .filter(
          (t) =>
            validMints.includes(t.info.mint) && t.info.tokenAmount.uiAmount > 0
        )
        .map((t) => {
          const mint = t.info.mint;
          const { name, image } = allowList[mint];
          return {
            type: "nft",
            account: t.pubkey.toBase58(),
            mint,
            amount: 1,
            name,
            image,
          } as MintOption;
        }),
    ];

    return result;
  }, [allowList, tokenAccounts]);
  const onSelectOption = useCallback(
    (event, value) => {
      if (value) {
        setSelectedOption(mintOptions.find((m) => m.account === value));
      }
    },
    [setSelectedOption, mintOptions]
  );

  useEffect(() => {
    if (mintOptions.length > 0) {
      setSelectedOption(mintOptions[0]);
    }
  }, [mintOptions, setSelectedOption]);

  // eslint-disable-next-line
  const needTxnExchange = useMemo(
    () => selectedOption?.type === "nft",
    [selectedOption]
  );
  const [exchangeTxn, setExchangeTxn] = useState<ExchangeTxnState>();

  const wallet = useWallet();
  const anchorWallet = useMemo(() => {
    if (
      !wallet ||
      !wallet.publicKey ||
      !wallet.signAllTransactions ||
      !wallet.signTransaction
    ) {
      return;
    }

    return {
      publicKey: wallet.publicKey,
      signAllTransactions: wallet.signAllTransactions,
      signTransaction: wallet.signTransaction,
    } as anchor.Wallet;
  }, [wallet]);

  const refreshCandyMachineState = useCallback(
    async (commitment: Commitment = "confirmed") => {
      if (!anchorWallet) {
        return;
      }
      if (props.error !== undefined) {
        setAlertState({
          open: true,
          message: props.error,
          severity: "error",
          hideDuration: null,
        });
        return;
      }

      const connection = new Connection(props.rpcHost, commitment);

      if (props.candyMachineId) {
        try {
          const cndy = await getCandyMachineState(
            anchorWallet,
            props.candyMachineId,
            connection
          );
          console.log("Candy machine state: ", cndy);
          let active = cndy?.state.goLiveDate
            ? cndy?.state.goLiveDate.toNumber() < new Date().getTime() / 1000
            : false;
          cndy.state.isLive = active;
          let presale = false;

          // duplication of state to make sure we have the right values!
          let isWLUser = false;
          let userPrice = cndy.state.price;

          const { value: tokenAccounts } =
            await connection.getParsedTokenAccountsByOwner(
              anchorWallet.publicKey,
              { programId: TOKEN_PROGRAM_ID }
            );
          setTokenAccounts(
            tokenAccounts
              .filter((t) => t.account.data.parsed)
              .map(
                ({
                  pubkey,
                  account: {
                    data: { parsed },
                  },
                }) => ({
                  pubkey,
                  info: parsed.info,
                })
              )
          );

          // whitelist mint?
          if (cndy?.state.whitelistMintSettings) {
            setIsWhitelistMint(true);
            // is it a presale mint?
            if (
              cndy.state.whitelistMintSettings.presale &&
              (!cndy.state.goLiveDate ||
                cndy.state.goLiveDate.toNumber() > new Date().getTime() / 1000)
            ) {
              presale = true;
            }
            // is there a discount?
            if (cndy.state.whitelistMintSettings.discountPrice) {
              setDiscountPrice(cndy.state.whitelistMintSettings.discountPrice);
              userPrice = cndy.state.whitelistMintSettings.discountPrice;
            } else {
              setDiscountPrice(undefined);
              // when presale=false and discountPrice=null, mint is restricted
              // to whitelist users only
              if (!cndy.state.whitelistMintSettings.presale) {
                cndy.state.isWhitelistOnly = true;
              }
            }
            // retrieves the whitelist token
            const mint = new anchor.web3.PublicKey(
              cndy.state.whitelistMintSettings.mint
            );
            const token = (
              await getAtaForMint(mint, anchorWallet.publicKey)
            )[0];

            try {
              const balance = await connection.getTokenAccountBalance(token);
              isWLUser = parseInt(balance.value.amount) > 0;
              // only whitelist the user if the balance > 0
              setIsWhitelistUser(isWLUser);
              setWhitelistTokenAccount(token);

              if (cndy.state.isWhitelistOnly) {
                active = presale || active;
              }
            } catch (e) {
              const isWhitelistUser = false;
              // const gumdropInfo = getGumdropInfo(
              //   anchorWallet.publicKey.toBase58()
              // );
              // if (gumdropInfo) {
              //   const program = await getProgram(connection, anchorWallet);
              //   const [distributorKey] = await fetchDistributor(
              //     program,
              //     gumdropInfo.distributor
              //   );
              //   const [claimStatus] = await getClaimStatus(
              //     gumdropInfo.index,
              //     distributorKey
              //   );
              //   const gumdropClaimed = await hasBeenClaimed(
              //     connection,
              //     claimStatus
              //   );
              //   setNeedTxnClaim(false && !gumdropClaimed);

              //   isWhitelistUser = true || !gumdropClaimed;
              // }
              setIsWhitelistUser(isWhitelistUser);
              setWhitelistTokenAccount(undefined);
              // no whitelist user, no mint
              // if (!isWhitelistUser && cndy.state.isWhitelistOnly) {
              //   active = false;
              // }
              console.log(
                "There was a problem fetching whitelist token balance"
              );
              console.log(e);
            }
          }
          userPrice = isWLUser ? userPrice : cndy.state.price;

          if (cndy?.state.tokenMint) {
            // retrieves the SPL token
            setIsSOLMint(false);
            const mint = new anchor.web3.PublicKey(cndy.state.tokenMint);
            const token = (
              await getAtaForMint(mint, anchorWallet.publicKey)
            )[0];
            try {
              const balance = await connection.getTokenAccountBalance(token);

              const valid = new anchor.BN(balance.value.amount).gte(userPrice);

              // only allow user to mint if token balance >  the user if the balance > 0
              setIsValidBalance(valid);
              active = active && valid;
            } catch (e) {
              setIsValidBalance(false);
              active = false;
              // no whitelist user, no mint
              console.log("There was a problem fetching SPL token balance");
              console.log(e);
            }
          } else {
            const balance = new anchor.BN(
              await connection.getBalance(anchorWallet.publicKey)
            );
            const valid =
              balance.toNumber() >= userPrice.toNumber() + FEE_PRICE;
            setIsValidBalance(valid);
            active = active && valid;
          }

          // datetime to stop the mint?
          if (cndy?.state.endSettings?.endSettingType.date) {
            setEndDate(toDate(cndy.state.endSettings.number));
            if (
              cndy.state.endSettings.number.toNumber() <
              new Date().getTime() / 1000
            ) {
              active = false;
            }
          }
          // amount to stop the mint?
          if (cndy?.state.endSettings?.endSettingType.amount) {
            const limit = Math.min(
              cndy.state.endSettings.number.toNumber(),
              cndy.state.itemsAvailable
            );
            if (cndy.state.itemsRedeemed < limit) {
              setItemsRemaining(limit - cndy.state.itemsRedeemed);
            } else {
              setItemsRemaining(0);
              cndy.state.isSoldOut = true;
            }
          } else {
            setItemsRemaining(cndy.state.itemsRemaining);
          }

          if (cndy.state.isSoldOut) {
            active = false;
          }

          const [collectionPDA] = await getCollectionPDA(props.candyMachineId);
          const collectionPDAAccount = await connection.getAccountInfo(
            collectionPDA
          );

          setIsActive((cndy.state.isActive = active));
          setIsPresale((cndy.state.isPresale = presale));
          setCandyMachine(cndy);

          const txnEstimate =
            892 +
            (!!collectionPDAAccount && cndy.state.retainAuthority ? 182 : 0) +
            (cndy.state.tokenMint ? 66 : 0) +
            (cndy.state.whitelistMintSettings ? 34 : 0) +
            (cndy.state.whitelistMintSettings?.mode?.burnEveryTime ? 34 : 0) +
            (cndy.state.gatekeeper ? 33 : 0) +
            (cndy.state.gatekeeper?.expireOnUse ? 66 : 0);

          setNeedTxnSplit(txnEstimate > 1230);
        } catch (e) {
          if (e instanceof Error) {
            if (
              e.message === `Account does not exist ${props.candyMachineId}`
            ) {
              setAlertState({
                open: true,
                message: `Couldn't fetch candy machine state. Tell the devs you want to mint!`,
                severity: "error",
                hideDuration: null,
              });
            } else if (
              e.message.startsWith("failed to get info about account")
            ) {
              setAlertState({
                open: true,
                message: `Couldn't fetch candy machine state. Tell the devs you want to mint!`,
                severity: "error",
                hideDuration: null,
              });
            }
          } else {
            setAlertState({
              open: true,
              message: `${e}`,
              severity: "error",
              hideDuration: null,
            });
          }
          console.log(e);
        }
      } else {
        setAlertState({
          open: true,
          message: `Couldn't fetch candy machine state. Tell the devs you want to mint!`,
          severity: "error",
          hideDuration: null,
        });
      }
    },
    [anchorWallet, props.candyMachineId, props.error, props.rpcHost]
  );

  const onMint = async (
    beforeTransactions: Transaction[] = [],
    afterTransactions: Transaction[] = []
  ) => {
    try {
      setIsUserMinting(true);
      if (wallet.connected && candyMachine?.program && wallet.publicKey) {
        let exchangeTxnState: ExchangeTxnState | undefined;
        if (needTxnExchange && exchangeTxn === undefined) {
          setAlertState({
            open: true,
            message: `Please sign the exchange of ${
              selectedOption!.name
            } for a ${WL_NAME}`,
            severity: "info",
          });
          exchangeTxnState = await exchangeNFTforWLToken(
            candyMachine.program.provider.connection,
            anchorWallet!,
            selectedOption!,
            whitelistTokenAccount
          );

          let status: any = { err: true };
          if (exchangeTxnState.transaction) {
            status = await awaitTransactionSignatureConfirmation(
              exchangeTxnState.transaction,
              props.txTimeout,
              props.connection,
              true
            );
          }
          if (status && !status.err) {
            setExchangeTxn(exchangeTxnState);
            setAlertState({
              open: true,
              message:
                "Exchange succeeded! Please now sign minting transaction",
              severity: "info",
            });
          } else {
            setAlertState({
              open: true,
              message: "Exchange failed! Please try again!",
              severity: "error",
            });
            return;
          }
        }
        let claimTxnState: ClaimTxnState | undefined;
        if (needTxnClaim && claimTxn === undefined) {
          setAlertState({
            open: true,
            message: "Please sign the WL tokens claim",
            severity: "info",
          });
          claimTxnState = await claimWLTokens(candyMachine, anchorWallet!);
          let status: any = { err: true };
          if (claimTxnState.transaction) {
            status = await awaitTransactionSignatureConfirmation(
              claimTxnState.transaction,
              props.txTimeout,
              props.connection,
              true
            );
          }
          if (status && !status.err) {
            setClaimTxn(claimTxnState);
            setAlertState({
              open: true,
              message:
                "WL claim succeeded! Please now sign minting transaction",
              severity: "info",
            });
          } else {
            setAlertState({
              open: true,
              message: "Claim failed! Please try again!",
              severity: "error",
            });
            return;
          }
        }

        let setupMint: SetupState | undefined;
        if (needTxnSplit && setupTxn === undefined) {
          setAlertState({
            open: true,
            message: "Please sign the pre-mint",
            severity: "info",
          });
          setupMint = await createAccountsForMint(
            candyMachine,
            wallet.publicKey
          );
          let status: any = { err: true };
          if (setupMint.transaction) {
            status = await awaitTransactionSignatureConfirmation(
              setupMint.transaction,
              props.txTimeout,
              props.connection,
              true
            );
          }
          if (status && !status.err) {
            setSetupTxn(setupMint);
            setAlertState({
              open: true,
              message:
                "Pre-mint succeeded! Please now sign the mint transaction",
              severity: "info",
            });
          } else {
            setAlertState({
              open: true,
              message: "Mint failed! Please try again!",
              severity: "error",
            });
            return;
          }
        } else {
          setAlertState({
            open: true,
            message: "Please sign the mint transaction",
            severity: "info",
          });
        }

        const mintResult = await mintOneToken(
          candyMachine,
          anchorWallet!,
          wallet.publicKey,
          beforeTransactions,
          afterTransactions,
          setupMint ?? setupTxn
        );

        let status: any = { err: true };
        let metadataStatus = null;
        if (mintResult) {
          status = await awaitTransactionSignatureConfirmation(
            mintResult.mintTxId,
            props.txTimeout,
            props.connection,
            true
          );

          metadataStatus =
            await candyMachine.program.provider.connection.getAccountInfo(
              mintResult.metadataKey,
              "processed"
            );
          console.log("Metadata status: ", !!metadataStatus);
        }

        if (status && !status.err && metadataStatus) {
          // manual update since the refresh might not detect
          // the change immediately
          const remaining = itemsRemaining! - 1;
          setItemsRemaining(remaining);
          setIsActive((candyMachine.state.isActive = remaining > 0));
          candyMachine.state.isSoldOut = remaining === 0;
          setExchangeTxn(undefined);
          setClaimTxn(undefined);
          setSetupTxn(undefined);
          setAlertState({
            open: true,
            message: "Mint successful!",
            severity: "success",
            hideDuration: 7000,
          });
          refreshCandyMachineState("processed");
        } else if (status && !status.err) {
          setAlertState({
            open: true,
            message:
              "Mint likely failed! Check the explorer to confirm the mint failed and if so, make sure you are eligible to mint before trying again.",
            severity: "error",
            hideDuration: 8000,
          });
          refreshCandyMachineState();
        } else {
          setAlertState({
            open: true,
            message: "Mint failed! Please try again!",
            severity: "error",
          });
          refreshCandyMachineState();
        }
      }
    } catch (error: any) {
      let message = error.msg || "Mint failed! Please try again!";
      if (!error.msg) {
        if (!error.message) {
          message = "Mint timed out! Please try again.";
        } else if (error.message.indexOf("0x137") >= 0) {
          console.log(error);
          message = `All NFTs have been minted!`;
        } else if (error.message.indexOf("0x135") >= 0) {
          message = `Insufficient funds to mint. Please fund your wallet.`;
        }
      } else {
        if (error.code === 311) {
          console.log(error);
          message = `All NFTs have been minted!`;
          window.location.reload();
        } else if (error.code === 312) {
          message = `Mint period hasn't started yet.`;
        }
      }

      setAlertState({
        open: true,
        message,
        severity: "error",
      });
      // updates the candy machine state to reflect the latest
      // information on chain
      refreshCandyMachineState();
    } finally {
      setIsUserMinting(false);
    }
  };

  const toggleMintButton = () => {
    if (!candyMachine) {
      return;
    }
    let active = !isActive || isPresale;

    if (active) {
      if (
        candyMachine!.state.isWhitelistOnly &&
        (!isWhitelistUser || mintOptions.length < 1)
      ) {
        active = false;
      }
      if (endDate && Date.now() >= endDate.getTime()) {
        active = false;
      }
    }

    if (
      isPresale &&
      candyMachine!.state.goLiveDate &&
      candyMachine!.state.goLiveDate.toNumber() <= new Date().getTime() / 1000
    ) {
      setIsPresale((candyMachine!.state.isPresale = false));
    }

    setIsActive((candyMachine!.state.isActive = active));
  };

  useEffect(() => {
    if (
      isActive &&
      candyMachine &&
      candyMachine.state.whitelistMintSettings &&
      !isWhitelistUser &&
      mintOptions.length < 1
    ) {
      setIsActive(false);
    }
  }, [isActive, candyMachine, isWhitelistUser, mintOptions, setIsActive]);

  useEffect(() => {
    refreshCandyMachineState();
  }, [
    anchorWallet,
    props.candyMachineId,
    props.connection,
    refreshCandyMachineState,
  ]);

  useEffect(() => {
    const id = setInterval(refreshCandyMachineState, 20000);
    refreshCandyMachineState();
    return () => clearInterval(id);
  }, [refreshCandyMachineState]);

  const closed =
    candyMachine?.state?.isSoldOut ||
    (endDate && Date.now() > endDate.getTime());

  return (
    <Container className="page-container">
      <Container
        maxWidth="xs"
        style={{ padding: "unset", position: "relative" }}
      >
        <Typography variant="h3" align="center" className="page-title">
          Seers
        </Typography>

        <Paper className="mint-box">
          <WalletMultiButton className="connect-button">
            {!wallet.connected ? "Connect Wallet" : undefined}
          </WalletMultiButton>
          {wallet.connected && (
            <>
              {candyMachine && (
                <div className="mint-info-grid">
                  <div className="mint-info">
                    <Typography variant="body2" color="textSecondary">
                      NFTs left
                    </Typography>
                    <Typography
                      variant="h6"
                      color="textPrimary"
                      style={{
                        fontWeight: "bold",
                      }}
                    >
                      {formatNumber.format(itemsRemaining)}
                    </Typography>
                  </div>
                  <div className="mint-info">
                    <Typography variant="body2" color="textSecondary">
                      Price
                    </Typography>
                    <Typography
                      variant="h6"
                      color="textPrimary"
                      style={{ fontWeight: "bold" }}
                    >
                      {!closed ? (
                        <>
                          {formatNumber.format(
                            candyMachine.state.price.toNumber() /
                              LAMPORTS_PER_SOL
                          )}{" "}
                          SOL
                        </>
                      ) : (
                        "--"
                      )}
                    </Typography>
                  </div>
                  <div className="mint-info-countdown">
                    {Date.now() / 1000 >
                      candyMachine.state.goLiveDate!.toNumber() &&
                    endDate &&
                    Date.now() < endDate.getTime() ? (
                      <Alert severity="info">
                        <AlertTitle>Hurry up!</AlertTitle>
                        <Typography align="center">Mint closes in</Typography>
                        <ThemeProvider theme={alternativeTheme}>
                          <MintCountdown
                            key="endSettings"
                            date={getCountdownDate(candyMachine)}
                            style={{ justifyContent: "center" }}
                            status="Mint closed"
                            onComplete={toggleMintButton}
                          />
                        </ThemeProvider>
                      </Alert>
                    ) : (
                      <>
                        {Date.now() / 1000 <
                          candyMachine.state.goLiveDate!.toNumber() && (
                          <Typography align="center">Mint opens in</Typography>
                        )}
                        <ThemeProvider theme={alternativeTheme}>
                          <MintCountdown
                            key="goLive"
                            date={getCountdownDate(candyMachine)}
                            style={{ justifyContent: "center" }}
                            status={closed ? "Mint closed" : "Live"}
                            onComplete={toggleMintButton}
                          />
                        </ThemeProvider>
                      </>
                    )}
                  </div>
                  {!closed && isWhitelistMint && mintOptions.length === 0 && (
                    <Alert className="mint-info-countdown" severity="warning">
                      <AlertTitle>Token required</AlertTitle>
                      You need a <strong>{WL_NAME} token</strong> to mint.
                    </Alert>
                  )}
                  {!closed && !isValidBalance && (
                    <Alert className="mint-info-countdown" severity="warning">
                      <AlertTitle>Insufficient balance</AlertTitle>
                      You should have{" "}
                      {candyMachine.state.price.toNumber() > 0 &&
                        `${formatNumber.format(
                          candyMachine.state.price.toNumber() / LAMPORTS_PER_SOL
                        )} SOL + `}
                      {FEE_PRICE} SOL (fees) to mint.
                    </Alert>
                  )}
                </div>
              )}
              {mintOptions.length > 0 && (
                <div className="mint-options-wrapper">
                  <Typography variant="h6" align="center">
                    Mint with
                  </Typography>
                  <div className="mint-options">
                    <ToggleButtonGroup
                      exclusive
                      onChange={onSelectOption}
                      value={selectedOption?.account}
                      orientation="vertical"
                      className="mint-options-group"
                    >
                      {mintOptions.map((m) => (
                        <ToggleButton
                          key={m.account}
                          value={m.account}
                          disabled={isUserMinting}
                          classes={{ label: "mint-option" }}
                        >
                          <img
                            src={m.image}
                            alt={m.name}
                            className="mint-option-image"
                          />
                          <Typography variant="h6">
                            {m.name}{" "}
                            {m.amount > 1 && <span>(x{m.amount})</span>}
                          </Typography>
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </div>
                </div>
              )}
              <div className="mint-container">
                <MintButton
                  candyMachine={candyMachine}
                  isMinting={isUserMinting}
                  setIsMinting={(val) => setIsUserMinting(val)}
                  onMint={onMint}
                  isActive={isActive}
                />
              </div>
            </>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={alertState.open}
        TransitionComponent={TransitionUp}
        autoHideDuration={
          alertState.hideDuration === undefined ? 5000 : alertState.hideDuration
        }
        onClose={() => setAlertState({ ...alertState, open: false })}
      >
        <Alert
          onClose={() => setAlertState({ ...alertState, open: false })}
          severity={alertState.severity}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

const getCountdownDate = (
  candyMachine: CandyMachineAccount
): Date | undefined => {
  if (
    candyMachine.state.isLive &&
    candyMachine.state.endSettings?.endSettingType.date
  ) {
    return toDate(candyMachine.state.endSettings.number);
  }

  return toDate(
    candyMachine.state.goLiveDate
      ? candyMachine.state.goLiveDate
      : candyMachine.state.isPresale
      ? new anchor.BN(new Date().getTime() / 1000)
      : undefined
  );
};

export default Home;
